import { useEffect, useMemo, useRef, useState } from 'react'
import { HiSelector } from 'react-icons/hi'
import { useLanguage } from '@/i18n/LanguageContext'

interface LanguageSwitcherProps {
  className?: string
  fullWidth?: boolean
}

export function LanguageSwitcher({ className = '', fullWidth = false }: LanguageSwitcherProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const { language, setLanguage, t } = useLanguage()

  const selected = useMemo(() => {
    return t.languageSelector.options.find((option) => option.value === language) ?? t.languageSelector.options[0]
  }, [language, t.languageSelector.options])

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (!rootRef.current) {
        return
      }
      if (event.target instanceof Node && !rootRef.current.contains(event.target)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', onClickOutside)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
    }
  }, [])

  return (
    <div ref={rootRef} className={`relative ${fullWidth ? 'w-full' : ''} ${className}`}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t.languageSelector.label}
        onClick={() => setOpen((prev) => !prev)}
        className={`h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 ${fullWidth ? 'w-full' : 'min-w-[220px]'}`}
      >
        <span className="flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 truncate">
            <img
              src={selected.icon}
              alt={selected.label}
              className="h-4 w-6 rounded-sm border border-slate-200 object-cover"
            />
            <span className="truncate">{selected.label}</span>
          </span>
          <HiSelector className="h-4 w-4 shrink-0" />
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t.languageSelector.label}
          className="absolute z-50 mt-2 max-h-64 overflow-auto rounded-lg border border-slate-200 bg-white p-1 shadow-lg w-full"
        >
          {t.languageSelector.options.map((option) => {
            const isSelected = option.value === language
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  setLanguage(option.value)
                  setOpen(false)
                }}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors ${
                  isSelected
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <img
                  src={option.icon}
                  alt={option.label}
                  className="h-4 w-6 rounded-sm border border-slate-200 object-cover"
                />
                <span>{option.label}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
